// A continuous-pitch ribbon: drag horizontally to glide pitch. Maps frequency
// on a LOG scale (equal screen space per octave), so the low end gets as much
// control as the high end. A second input method next to the keyboard.
//   - "Free" mode glides continuously, snapped to 0.1 Hz, recorded as
//     fractional pitch so slides aren't rounded away.
//   - "Notes" mode locks to semitones (stays in tune).
//   - "Scale" mode locks to the harmony bar's key + scale, so a slide stays in
//     tune *and* in key — the two input areas then agree with each other.
//     With a fixed chord shape selected there is no key context, so it falls
//     back to semitones and says so in the readout.

import { midiToFreq } from "../../engine/Synth";
import type { HarmonySettings } from "../../engine/harmony";

const NOTE_NAMES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
const DECADES = [1, 10, 100, 1000, 10000]; // Hz reference lines

// Local copy of the scale tables. `harmony.ts` keeps its own private copy and
// exports no interval data, so duplicating the three rows here is cheaper than
// widening that module's API. Keep in sync if a scale is ever added there.
const SCALE_STEPS: Record<string, readonly number[]> = {
  major: [0, 2, 4, 5, 7, 9, 11],
  minor: [0, 2, 3, 5, 7, 8, 10],
  pentatonic: [0, 2, 4, 7, 9],
};
const SCALE_LABELS: Record<string, string> = {
  major: "Major",
  minor: "Minor",
  pentatonic: "Pent",
};

const MODES = ["free", "notes", "scale"] as const;
type RibbonMode = (typeof MODES)[number];

// Extra travel (in semitones) needed to leave the note you're already on. A
// plain nearest-neighbour snap oscillates when the pointer sits on a midpoint;
// this makes the current note sticky until you're clearly past the boundary.
const SNAP_HYSTERESIS = 0.5;

function noteName(midi: number): string {
  const m = Math.round(midi);
  return `${NOTE_NAMES[((m % 12) + 12) % 12]}${Math.floor(m / 12) - 1}`;
}

function freqToMidi(freq: number): number {
  return 69 + 12 * Math.log2(freq / 440);
}

function fmtHz(freq: number): string {
  return freq >= 1000 ? `${(freq / 1000).toFixed(2)} kHz` : `${freq.toFixed(1)} Hz`;
}

/** Nearest tone of `steps` (a pitch-class set on `root`) to a fractional MIDI.
 *  Scans the neighbouring octaves so the wrap at the top of the scale works. */
function nearestScaleMidi(midi: number, root: number, steps: readonly number[]): number {
  const base = Math.floor((midi - root) / 12);
  let best = Math.round(midi);
  let bestDist = Infinity;
  for (let o = base - 1; o <= base + 1; o++) {
    for (const s of steps) {
      const cand = root + o * 12 + s;
      const d = Math.abs(midi - cand);
      if (d < bestDist) {
        bestDist = d;
        best = cand;
      }
    }
  }
  return best;
}

export interface RibbonOptions {
  lowHz: number;
  highHz: number;
  onDown: (midi: number) => void;
  onMove: (midi: number) => void;
  onUp: () => void;
  /** Live read of the harmony settings (the keyboard's key + scale). Only used
   *  by "Scale" mode; without it the toggle stays Free/Notes only. */
  getHarmony?: () => HarmonySettings;
}

export interface RibbonController {
  /** Mirror the keyboard: show the cursor/readout at this pitch (null leaves a
   *  ghost of the last note). Ignored while the ribbon itself is being dragged. */
  setExternal(midi: number | null): void;
  /** Re-read the harmony settings so the mode chip's key label stays truthful
   *  after the harmony bar changes. Safe to call at any time. */
  refresh(): void;
}

export function buildRibbon(container: HTMLElement, opts: RibbonOptions): RibbonController {
  container.classList.add("ribbon");
  container.innerHTML = "";
  const logLow = Math.log(opts.lowHz);
  const logHigh = Math.log(opts.highHz);
  const logSpan = logHigh - logLow;
  let mode: RibbonMode = "notes";
  let active = false;
  // The tone the current drag is sitting on, for snap hysteresis. Cleared
  // between drags so every fresh press lands on the true nearest tone.
  let heldSnap: number | null = null;

  const freqToT = (freq: number): number => (Math.log(freq) - logLow) / logSpan;

  // Hz decade guide lines.
  for (const hz of DECADES) {
    if (hz < opts.lowHz || hz > opts.highHz) continue;
    const guide = document.createElement("div");
    guide.className = "ribbon-guide";
    guide.style.left = `${freqToT(hz) * 100}%`;
    guide.textContent = fmtHz(hz);
    container.append(guide);
  }

  const cursor = document.createElement("div");
  cursor.className = "ribbon-cursor";
  cursor.hidden = true;
  container.append(cursor);

  // Faint trace of the last note, mirrored from the keyboard / left after a slide.
  const ghostCursor = document.createElement("div");
  ghostCursor.className = "ribbon-cursor ghost";
  ghostCursor.hidden = true;
  container.append(ghostCursor);

  // Big always-readable note/frequency readout while sliding.
  const readout = document.createElement("div");
  readout.className = "ribbon-readout";
  readout.hidden = true;
  container.append(readout);

  let ghostMidi: number | null = null;

  // The scale the ribbon should follow, or null when there isn't one (no
  // harmony wired, or a fixed chord shape is selected — a shape has no key).
  const currentScale = (): { root: number; steps: readonly number[]; label: string } | null => {
    const h = opts.getHarmony?.();
    if (!h) return null;
    const [kind, name] = h.chord.split(":");
    if (kind !== "scale") return null;
    const steps = SCALE_STEPS[name];
    if (!steps) return null;
    const root = ((Math.round(h.root) % 12) + 12) % 12;
    return { root, steps, label: `${NOTE_NAMES[root]} ${SCALE_LABELS[name] ?? name}` };
  };

  const toggle = document.createElement("button");
  toggle.className = "ribbon-mode";
  // The chip keeps one short word in every mode — the readout carries the key
  // detail, so the chip's box never grows into the centred readout on a phone.
  const paintToggle = (): void => {
    const scale = mode === "scale" ? currentScale() : null;
    toggle.textContent = mode === "free" ? "Free" : mode === "notes" ? "Notes" : "Scale";
    toggle.classList.toggle("is-scale", mode === "scale" && !!scale);
    toggle.classList.toggle("is-nokey", mode === "scale" && !scale);
    toggle.title =
      mode !== "scale"
        ? "Snap: Free · Notes · Scale"
        : scale
          ? `Snapping to ${scale.label}`
          : "No key to follow — pick an in-key scale in the harmony bar. Snapping to semitones for now.";
  };
  paintToggle();
  toggle.addEventListener("pointerdown", (e) => {
    e.stopPropagation();
    e.preventDefault();
  });
  toggle.addEventListener("click", () => {
    mode = MODES[(MODES.indexOf(mode) + 1) % MODES.length];
    heldSnap = null;
    paintToggle();
  });
  container.append(toggle);

  // Returns a (possibly fractional) MIDI value for the engine.
  const midiAt = (clientX: number): number => {
    const rect = container.getBoundingClientRect();
    const t = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    const freq = Math.exp(logLow + t * logSpan);
    if (mode === "notes") return Math.round(freqToMidi(freq));
    if (mode === "scale") {
      const raw = freqToMidi(freq);
      const scale = currentScale();
      // No key to follow → semitones, same as Notes (the readout says so).
      const cand = scale ? nearestScaleMidi(raw, scale.root, scale.steps) : Math.round(raw);
      // Hysteresis: only leave the note we're on once another one is clearly
      // closer, so a pointer resting on a boundary can't flutter between two.
      if (heldSnap !== null && cand !== heldSnap) {
        if (Math.abs(raw - cand) + SNAP_HYSTERESIS >= Math.abs(raw - heldSnap)) return heldSnap;
      }
      heldSnap = cand;
      return cand;
    }
    const snappedHz = Math.round(freq * 10) / 10; // 0.1 Hz steps
    return freqToMidi(snappedHz);
  };

  const showActive = (midi: number): void => {
    const freq = midiToFreq(midi);
    cursor.style.left = `${freqToT(freq) * 100}%`;
    cursor.hidden = false;
    let tag = "";
    if (mode === "scale") {
      const scale = currentScale();
      tag = scale
        ? ` <i class="ribbon-key">${scale.label}</i>`
        : ` <i class="ribbon-key is-warn">no key · semitones</i>`;
    }
    readout.innerHTML = `<b>${noteName(midi)}</b> ${fmtHz(freq)}${tag}`;
    readout.hidden = false;
    ghostMidi = midi;
    ghostCursor.hidden = true;
  };
  const toGhost = (): void => {
    cursor.hidden = true;
    readout.hidden = true;
    if (ghostMidi === null) return;
    ghostCursor.style.left = `${freqToT(midiToFreq(ghostMidi)) * 100}%`;
    ghostCursor.textContent = noteName(ghostMidi);
    ghostCursor.hidden = false;
  };

  container.addEventListener("pointerdown", (e) => {
    e.preventDefault();
    container.setPointerCapture(e.pointerId);
    active = true;
    heldSnap = null; // a fresh press takes the true nearest tone
    paintToggle(); // harmony may have changed since the last touch
    const midi = midiAt(e.clientX);
    showActive(midi);
    opts.onDown(midi);
  });
  container.addEventListener("pointermove", (e) => {
    if (!active) return;
    const midi = midiAt(e.clientX);
    showActive(midi);
    opts.onMove(midi);
  });
  const end = (): void => {
    if (!active) return;
    active = false;
    heldSnap = null;
    toGhost();
    opts.onUp();
  };
  container.addEventListener("pointerup", end);
  container.addEventListener("pointercancel", end);

  return {
    setExternal(midi: number | null): void {
      if (active) return; // a live drag wins over keyboard mirroring
      if (midi === null) toGhost();
      else showActive(midi);
    },
    refresh(): void {
      paintToggle();
    },
  };
}
